import React, { useEffect, useRef, useState } from 'react';
import { ConfigData, ConnectionStatus } from '../../lib/types';
import { CONN_DOT_CLASS, CONN_TEXT } from '../../lib/constants';
import { TrialCountdownPill } from './TrialCountdownPill';
import type { SubscriptionStatus } from '../../lib/types';

interface Tab { id: string; label: string; }
interface AppHeaderProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (id: string) => void;

  userEmail: string;
  configData: ConfigData | null;
  connStatus: ConnectionStatus;
  onReconnect: () => void;

  isDark: boolean;
  setIsDark: (v: boolean) => void;
  onSignOut: () => void;
  onOpenSettings: () => void;
  onOpenAdmin?: () => void;
}

const roleLabel = (r: string) =>
  r === 'super_admin' ? 'Super Admin' :
  r === 'org_admin'   ? 'Account Owner' :
  r === 'dept_admin'  ? 'Team Member' : r;

export function AppHeader({
  tabs, activeTab, onTabChange,
  userEmail, configData, connStatus, onReconnect,
  isDark, setIsDark,
  onSignOut, onOpenSettings, onOpenAdmin,
}: AppHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const initials = (configData?.profile?.display_name || userEmail || 'U')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const userRole = configData?.profile?.role || '';

  return (
    <header>
      <div className="hmark">
        <svg viewBox="0 0 96 96" fill="none">
          <rect x="24" y="28" width="48" height="56" rx="8" fill="rgba(255,255,255,0.16)" />
          <rect x="38" y="28" width="20" height="9" fill="#16A34A" />
          <rect x="38" y="18" width="20" height="19" rx="5" fill="rgba(255,255,255,0.92)" />
          <rect x="31" y="44" width="34" height="3" rx="1.5" fill="rgba(255,255,255,0.4)" />
          <rect x="31" y="52" width="22" height="3" rx="1.5" fill="rgba(255,255,255,0.4)" />
          <path d="M31 69 L61 69" stroke="white" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M53 63 L61 69 L53 75" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>
      <div className="hname">GoPal</div>
      <div style={{ width: 36 }} />

      {tabs.map(t => (
        <button
          key={t.id}
          className={`tab${activeTab === t.id ? ' active' : ''}`}
          onClick={() => onTabChange(t.id)}
        >
          {t.label}
        </button>
      ))}

      <div style={{ flex: 1 }} />

      <div className="hright">
        {configData?.subscription_status && configData.trial_ends_at !== undefined && (
          <TrialCountdownPill
            subscriptionStatus={configData.subscription_status as SubscriptionStatus}
            trialEndsAt={configData.trial_ends_at ?? null}
            userRole={userRole}
          />
        )}

        <div className="relative" ref={avatarRef}>
          <button
            className="avatar"
            onClick={() => setMenuOpen(v => !v)}
            title="Account menu"
          >{initials}</button>

          <div
            className={CONN_DOT_CLASS[connStatus] || 'hdot'}
            title={CONN_TEXT[connStatus]}
            style={{
              position: 'absolute', bottom: -1, right: -1,
              width: 8, height: 8, borderRadius: '50%',
              border: '2px solid var(--surface)',
              cursor: connStatus === 'offline' ? 'pointer' : 'default',
            }}
            onClick={() => { if (connStatus === 'offline') onReconnect(); }}
          />

          {menuOpen && (
            <div
              className="menu"
              style={{
                position: 'absolute',
                right: 0, top: 38, width: 240,
                padding: 6,
              }}
            >
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                <div className="text-base font-bold" style={{ marginBottom: 2, color: 'var(--text)' }}>
                  {configData?.profile?.display_name || userEmail}
                </div>
                <div className="text-xs text-subtle" style={{ marginBottom: 6 }}>{userEmail}</div>
                <div className="flex gap-1 flex-wrap">
                  {configData?.profile?.role && (
                    <span className="badge badge-accent">
                      {roleLabel(configData.profile.role)}
                    </span>
                  )}
                  {configData?.profile?.department && (
                    <span
                      className="badge"
                      style={{
                        background: 'var(--surface2)', color: 'var(--text2)',
                        fontWeight: 500, textTransform: 'none', letterSpacing: 0,
                      }}
                    >{configData.profile.department}</span>
                  )}
                </div>
              </div>

              <div style={{ padding: 4 }}>
                <div
                  className="flex items-center justify-between"
                  style={{ padding: '8px 10px', borderRadius: 'var(--r)' }}
                >
                  <span className="text-sm text-muted">Dark mode</span>
                  <button
                    className={`switch ${isDark ? 'is-on' : ''}`}
                    onClick={() => setIsDark(!isDark)}
                    title="Toggle dark / light mode"
                  />
                </div>

                <button className="menu-item" onClick={() => { onOpenSettings(); setMenuOpen(false); }}>
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.3">
                    <circle cx="8" cy="8" r="2.5" />
                    <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.1 3.1l1.4 1.4M11.5 11.5l1.4 1.4M3.1 12.9l1.4-1.4M11.5 4.5l1.4-1.4" />
                  </svg>
                  Settings
                </button>

                {onOpenAdmin && (
                  <button
                    className="menu-item"
                    onClick={() => { onOpenAdmin(); setMenuOpen(false); }}
                  >
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.3">
                      <path d="M8 1.5l5.5 2.5v4c0 3-2.3 5.7-5.5 6.5-3.2-.8-5.5-3.5-5.5-6.5V4l5.5-2.5z" />
                    </svg>
                    Switch to Admin →
                  </button>
                )}

                <button
                  className="menu-item is-danger"
                  onClick={async () => { await onSignOut(); setMenuOpen(false); }}
                  style={{ fontWeight: 600 }}
                >
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.3">
                    <path d="M6 2H4a2 2 0 00-2 2v8a2 2 0 002 2h2M10 12l4-4-4-4M7 8h7" />
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
